import { useFetchPromotions, useLanguages } from '~/composables';
import { PromotionProduct } from '~/composables/useFetchPromotions';
import { useContext } from '@nuxtjs/composition-api';
import type {
  DataLoader,
  LoadFunction,
  PreloadedData,
  PreloadedDataItem,
  SectionEnhancedProps,
  SectionProps,
} from './types';

const formatPrice = (price: number, locale: string, currency: string) =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(price);

const propsEnhancer = (
  sectionProps: SectionProps,
  payload = [] as PreloadedData,
): SectionEnhancedProps => {
  const items = payload.map((item) => {
    return {
      image: {
        src: item.imageSrc,
        alt: item.imageAlt,
      },
      name: item.name,
      sku: item.sku,
      price: item.price,
      link: item.link,
    };
  });

  return { ...sectionProps, items };
};

const getEnhancerDependency = (sectionProps: SectionProps) => {
  return sectionProps.promoId;
};

const useCatalogSectionPromo: DataLoader = () => {
  const { currentLanguage } = useLanguages();
  const {
    $config: { preferProductCatalogFlag },
  } = useContext();
  const { fetchPromotionById } = useFetchPromotions();

  const productCardLinkRoot = preferProductCatalogFlag
    ? '/product'
    : '/solution';

  const mapProductsPayload = (
    searchedProduct: PromotionProduct,
  ): PreloadedDataItem => ({
    imageSrc: searchedProduct.images[0],
    imageAlt: searchedProduct.name,
    name: searchedProduct.name,
    sku: searchedProduct.sku,
    price: searchedProduct.price
      ? `${formatPrice(
          searchedProduct.price,
          currentLanguage.value?.code || 'en',
          searchedProduct.currency,
        )}`
      : '',
    link: `${productCardLinkRoot}/${searchedProduct.slug}`,
  });

  const load: LoadFunction = async (sectionProps) => {
    const promotionId = getEnhancerDependency(sectionProps);

    if (!promotionId) return sectionProps as SectionEnhancedProps;

    const catalogPayload = (await fetchPromotionById(promotionId))?.map(
      mapProductsPayload,
    );

    return propsEnhancer(sectionProps, catalogPayload);
  };

  return {
    load,
  };
};

export default useCatalogSectionPromo;
